import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faEllipsisV,
  faEye,
  faPencil,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";

/* ------------------------------------------------------
   GLOBAL SEARCH
------------------------------------------------------ */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value || undefined)}
    placeholder="Search..."
    className="form-control"
  />
);

/* ------------------------------------------------------
   TABLE COMPONENT
------------------------------------------------------ */
const TableComponent = ({
  columns,
  data,
  globalFilter,
  setGlobalFilter,
  pageSize,
  setPageSize,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { pageIndex } = state;

  return (
    <>
      <table
        className="table table-striped"
        {...getTableProps()}
        style={{ fontSize: "13px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    style={{
                      color:
                        row.original.status === "Pending"
                          ? "#0d6efd"
                          : row.original.status === "Approved"
                          ? "#198754"
                          : "inherit",
                    }}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* PAGINATION */}
      <div className="d-flex justify-content-between align-items-center">
        <button
          className="btn btn-secondary"
          onClick={() => previousPage()}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of{" "}
          <strong>{pageOptions.length}</strong>
        </span>

        <button
          className="btn btn-secondary"
          onClick={() => nextPage()}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* ------------------------------------------------------
   MAIN COMPONENT
------------------------------------------------------ */
const DeductionsLedger = ({
  accessOfDepartments,
  accessedLedgers,
  permissions,
}) => {
  const navigate = useNavigate();
  const code = localStorage.getItem("code");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageSize, setPageSize] = useState(25);

  /* -------------------- LOAD DATA -------------------- */
  const loadData = async () => {
    setLoading(true);

    try {
      if (statusFilter === "pending") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getPendingLessDraftsLedger",
          { params: { code, accessedLedgers } }
        );
        setTableData(data);
      } else if (statusFilter === "approved") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getApprovedLessDraftsLedger",
          { params: { code, accessedLedgers } }
        );
        setTableData(data);
      } else {
        const [pRes, aRes] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getPendingLessDraftsLedger", {
            params: { code, accessedLedgers },
          }),
          apiClient.get(SERVER_BASE + "/getApprovedLessDraftsLedger", {
            params: { code, accessedLedgers },
          }),
        ]);
        setTableData([...pRes.data, ...aRes.data]);
      }
    } catch (err) {
      console.error("Error loading deductions ledger:", err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* -------------------- STATUS FILTER -------------------- */
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },

      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => (value ? moment(value).format("DD-MMM-YY") : ""),
      },

      { Header: "Ref No.", accessor: "draftCode" },
      { Header: "Ledger", accessor: "ledgerName" },
      { Header: "Event", accessor: "purpose" },

      {
        Header: "Inventory Data",
        accessor: "inventory_data",
        Cell: ({ value }) => {
          let parsed = typeof value === "string" ? JSON.parse(value) : value;
          return parsed?.map((i) => i.item).join(", ");
        },
      },

      { Header: "Added By", accessor: "createdBy" },
      { Header: "Approved By", accessor: "approvedBy" },

      { Header: "Status", accessor: "status" },
      {
        Header: "Preview",
        accessor: "Preview",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            permissions?.read && (
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() =>
                  navigate("../../PreviewInventoryLedger", {
                    state: { id: r.id },
                  })
                }
              >
                View
              </button>
            )
          );
        },
      },
      {
        Header: "#",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="dropdown">
              <button className="btn btn-light" data-bs-toggle="dropdown">
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {/* Preview */}
                {/* <li>
                  <button
                    className="dropdown-item"
                    onClick={() =>
                      navigate("../../PreviewInventoryLedger", {
                        state: { id: r.id },
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                </li> */}

                {/* Edit only when Pending */}
                {r.status === "Pending" && permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() =>
                        navigate("../LessInventoryLedger", {
                          state: { rowId: r.id },
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faPencil} /> Edit
                    </button>
                  </li>
                )}
                <li>
                  <hr />
                </li>
                <li>
                  {r.createdBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPlus} /> {r.createdBy}
                      {r.created_at &&
                        ` (${moment(r.created_at).format(
                          "DD-MMM-YY hh:mm A"
                        )})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.updatedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPencil} /> {r.updatedBy}
                      {r.updatedAt &&
                        ` (${moment(r.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.approvedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faCheckCircle} /> {r.approvedBy}
                      {r.approvedAt &&
                        ` (${moment(r.approvedAt).format(
                          "DD-MMM-YY hh:mm A"
                        )})`}
                    </li>
                  )}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <h4>Deductions Ledger</h4>

      {/* TOP CONTROLS */}
      <div className="row align-items-center mb-3">
        {/* Add Button */}
        {permissions?.write && (
          <div className="col-md-2 mb-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../LessInventoryLedger")}
            >
              Less Inventory
            </button>
          </div>
        )}

        {/* Search */}
        <div className="col-md-5 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        {/* Status Filter */}
        <div className="col-md-3 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find((s) => s.value === statusFilter)}
            onChange={(e) => setStatusFilter(e.value)}
          />
        </div>

        {/* Page Size */}
        <div className="col-md-2 mb-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[25, 50, 100, 200].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={tableData}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default DeductionsLedger;
